import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiStatusManager, ApiStatus } from '../services/ApiStatusManager';

interface ApiStatusContextType {
    apiStatus: ApiStatus;
    isApiDown: boolean; // For backward compatibility
    isMaintenance: boolean;
    setApiDown: (isDown: boolean) => void;
}

const ApiStatusContext = createContext<ApiStatusContextType | undefined>(undefined);

/**
 * Provider for API status context
 * Tracks whether the API is down (connection refused) or under maintenance (503)
 */
export const ApiStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [apiStatus, setApiStatus] = useState<ApiStatus>('up');

    useEffect(() => {
        // Subscribe to API status changes from the manager
        const unsubscribe = apiStatusManager.subscribe((status) => {
            setApiStatus(status);
        });

        return unsubscribe;
    }, []);

    const setApiDown = (isDown: boolean) => {
        apiStatusManager.setApiDown(isDown);
    };

    return (
        <ApiStatusContext.Provider value={{
            apiStatus,
            isApiDown: apiStatus === 'down',
            isMaintenance: apiStatus === 'maintenance',
            setApiDown
        }}>
            {children}
        </ApiStatusContext.Provider>
    );
};

/**
 * Hook to access API status context
 */
export const useApiStatus = (): ApiStatusContextType => {
    const context = useContext(ApiStatusContext);
    if (context === undefined) {
        throw new Error('useApiStatus must be used within an ApiStatusProvider');
    }
    return context;
};
